import React, { useState, useEffect } from 'react';
import { Card } from '@shopify/polaris';

import { DashboardKPI } from '../Dashboard/DashboardHeader';
import { currencyFormatter, numberFormatter, percentFormatter } from '../../utils/formatters'
import { useShopProvider } from '../../components/ShopProvider';
import { useStandaloneCartAggregates } from '../../hooks/useStandaloneCartsKPI';
import "./styles.css";

export const StandaloneCartKPIObject = (currencyCode) => {
      return ({
            standalonecarts_count: {
                  name: 'Number of Carts',
                  value: 0,
                  render: (value) => numberFormatter().format(value),
                  isAgg: true,
                  display: true,
                  description: 'Number of standalone carts created',
                  comparisonColor: (ratio) => ratio > 1.0 ? 'success' : ratio < 1.0 ? 'critical' : null,
            },
            orders_count: {
                  name: 'Number of Orders',
                  value: 0,
                  render: (value) => numberFormatter().format(value),
                  isAgg: true,
                  display: true,
                  description: 'Number of orders generated from Standalone Carts',
                  comparisonColor: (ratio) => ratio > 1.0 ? 'success' : ratio < 1.0 ? 'critical' : null,
            },
            orders_total: {
                  name: 'Total Sales',
                  value: 0,
                  render: (value) => currencyFormatter(currencyCode).format(value),
                  isAgg: true,
                  display: true,
                  description: 'Total sales generated from Standalone Carts',
                  comparisonColor: (ratio) => ratio > 1.0 ? 'success' : ratio < 1.0 ? 'critical' : null,
            },
            average_order: {
                  name: 'AOV',
                  value: 0,
                  render: (value) => currencyFormatter(currencyCode).format(value),
                  display: true,
                  description: 'Average Order Value for orders generated from Standalone Carts',
                  comparisonColor: (ratio) => ratio > 1.0 ? 'success' : ratio < 1.0 ? 'critical' : null,
            },
            conversion_rate: {
                  name: 'Conversion Rate',
                  value: 0,
                  render: (value) => percentFormatter().format(value),
                  display: true,
                  description: 'Ratio of standalone carts resulting in a sale vs all standalone carts',
                  comparisonColor: (ratio) => ratio > 1.0 ? 'success' : ratio < 1.0 ? 'critical' : null,
            },
      })
};

const StandaloneCartDashboard = ({ filter }) => {
      const [kpis, setKpis] = useState(StandaloneCartKPIObject());
      const { shopDataLoading, shopData, shopOrigin } = useShopProvider();
      const { aggregates, loading } = useStandaloneCartAggregates(shopOrigin, filter);

      useEffect(() => {
            if (shopData && aggregates) {
                  const kpis = StandaloneCartKPIObject(shopData.currencyCode);

                  kpis.standalonecarts_count.value = aggregates.totalStandaloneCarts;
                  kpis.standalonecarts_count.previousValue = aggregates.totalStandaloneCartsPrevious;

                  kpis.orders_count.value = aggregates.totalOrders;
                  kpis.orders_count.previousValue = aggregates.totalOrdersPrevious;

                  kpis.orders_total.value = aggregates.totalOrderAmount;
                  kpis.orders_total.previousValue = aggregates.totalOrderAmountPrevious;

                  kpis.average_order.value = aggregates.totalOrders ? (aggregates.totalOrderAmount / aggregates.totalOrders) : null;
                  kpis.average_order.previousValue = aggregates.totalOrdersPrevious ? (aggregates.totalOrderAmountPrevious / aggregates.totalOrdersPrevious) : null;

                  kpis.conversion_rate.value = (aggregates.totalOrdersCreated && aggregates.totalOrderAmount > 0)
                        ? (aggregates.totalOrders / aggregates.totalOrdersCreated)
                        : 0;
                  kpis.conversion_rate.previousValue = (aggregates.totalOrdersCreatedPrevious && aggregates.totalOrderAmountPrevious > 0)
                        ? (aggregates.totalOrdersPrevious / aggregates.totalOrdersCreatedPrevious)
                        : 0;

                  setKpis(kpis);
            }
      }, [filter, aggregates, shopData]);

      return (
            <Card>
                  <div className="standalone-cart-dashboard-header">
                        {Object.keys(kpis).filter(kpi => kpis[kpi].display).map(kpi => <DashboardKPI key={kpi} kpi={kpis[kpi]} isLoading={loading || shopDataLoading} />)}
                  </div>
            </Card>
      )
}

export default StandaloneCartDashboard;
